/**
 * Whether the resolved sales person should be written onto the enquiry, and who.
 *
 * `sales_person.get_salesperson` answers who handles a customer at a company. Two situations
 * call for applying that answer, and they differ in one thing:
 *
 *   - the enquiry has NO sales person — fill it (the original `SalesPersonDefaulter` case);
 *   - the customer or company was CHANGED on this screen (Akhil, 2026-09-14) — the person on the
 *     enquiry handled the previous customer, so the server's answer for the new one replaces
 *     them, even though the field is not empty.
 *
 *   - the customer or company was CHANGED and the lookup ANSWERED with nobody: the person is
 *     KEPT. This used to clear the field (Akhil, 2026-09-15 — the person handled the previous
 *     customer); reversed 2026-09-18, because `sales_person` became mandatory on the quotation
 *     on 09-17 and is gated on RFQ send and quotation build — a cleared field stopped every next
 *     step with "the server requires sales person" right after "Sales person cleared". The
 *     caller says nobody is assigned and that the person was kept; the purchaser corrects it if
 *     it matters (`SalesPersonDefaulter` says so, from the lookup's own `resolvedNobody`).
 *
 * What never happens: replacing a chosen person on a plain page open (nothing changed, so the
 * choice stands), or writing anything while the lookup is still loading or has failed.
 */
export function decideSalesPerson({
  current,
  recommended,
  pairChanged,
}: {
  /** The sales person on the enquiry now, `''` when none. */
  current: string
  /** The one person the lookup resolved, or `null` when it resolved none or several. */
  recommended: string | null
  /** Whether customer or company differs from what the enquiry had when this screen opened. */
  pairChanged: boolean
}): string | null {
  // Nobody, or several: nothing to write. Never `''` — see the header.
  if (!recommended) return null
  if (recommended === current) return null
  if (current.trim() === '' || pairChanged) return recommended
  return null
}
